import { Coding, Condition, Immunization, Observation } from "@medplum/fhirtypes";
import { Smart } from "@metriport/fhir-sdk";
import dayjs from "dayjs";
import { getFirstCodeSpecified } from "../../shared-components/fhir-sections/shared";
import { MedicationWithRefs } from "./medication-types";

export type BaseSeries<T> = {
  code: string;
  codeDisplay: string;
  coding?: Coding;
  resourceType: string;
  resources: T[];
  latestDate: string;
  earliestDate: string;
};

export type ConditionSeries = BaseSeries<Smart<Condition>>;
export type ObservationSeries = BaseSeries<Smart<Observation>>;
export type ImmunizationSeries = BaseSeries<Smart<Immunization>>;
export type MedicationSeries = BaseSeries<MedicationWithRefs>;

/**
 * Groups resources by their code into series, ordered oldest to newest within each series
 */
export function groupResourcesByCode<T>(
  resources: T[],
  getCodeFn: (resource: T) => string,
  getDateFn: (resource: T) => string,
  resourceType: string
): BaseSeries<T>[] {
  const seriesMap = new Map<string, BaseSeries<T>>();

  // Group resources by code
  for (const resource of resources) {
    const code = getCodeFn(resource);
    const date = getDateFn(resource);

    // Create an empty series for the code if it doesn't exist
    if (!seriesMap.has(code)) {
      seriesMap.set(code, {
        code,
        codeDisplay: code, // Will be updated with proper display name
        resourceType,
        resources: [],
        latestDate: date,
        earliestDate: date,
      });
    }

    const series = seriesMap.get(code);
    if (!series) {
      throw new Error("Series not found");
    }
    series.resources.push(resource);

    // Update date range
    if (dayjs(date).isAfter(dayjs(series.latestDate))) {
      series.latestDate = date;
    }
    if (dayjs(date).isBefore(dayjs(series.earliestDate))) {
      series.earliestDate = date;
    }
  }

  // Sort resources within each series by date (oldest to newest)
  for (const series of seriesMap.values()) {
    series.resources.sort((a, b) => {
      const dateA = dayjs(getDateFn(a));
      const dateB = dayjs(getDateFn(b));
      return dateA.diff(dateB); // oldest first
    });
  }

  // Return series sorted by latest date (newest series first)
  return Array.from(seriesMap.values()).sort((a, b) => {
    const dateA = dayjs(a.latestDate);
    const dateB = dayjs(b.latestDate);
    return dateB.diff(dateA); // newest series first
  });
}

/**
 * Get condition code for grouping
 */
export function getConditionCode(condition: Condition): string {
  const coding = getConditionCoding(condition);
  if (coding) {
    return `${coding.system}: ${coding.code}`;
  }
  return condition.code?.text || condition.code?.coding?.[0]?.display || "Unknown";
}

/**
 * Get condition coding object
 */
export function getConditionCoding(condition: Condition): Coding | undefined {
  return getFirstCodeSpecified(condition.code?.coding ?? [], ["icd-10", "snomed"]);
}

/**
 * Get condition code for grouping
 */
export function getIcd10PrefixCode(condition: Condition, prefixLength = 3): string {
  const coding = getFirstCodeSpecified(condition.code?.coding ?? [], ["icd-10"]);
  const code = coding?.code;
  if (code) {
    return code.replace(/\./g, "").substring(0, prefixLength);
  }
  return "";
}

/**
 * Get observation code for grouping
 */
export function getObservationCode(observation: Observation): string {
  const coding = getObservationCoding(observation);
  if (coding) {
    return `${coding.system}: ${coding.code}`;
  }
  return observation.code?.text || observation.code?.coding?.[0]?.display || "Unknown";
}

/**
 * Get observation coding object
 */
export function getObservationCoding(observation: Observation): Coding | undefined {
  return getFirstCodeSpecified(observation.code?.coding ?? [], ["loinc", "snomed"]);
}

/**
 * Get immunization code for grouping
 */
export function getImmunizationCode(immunization: Immunization): string {
  const coding = getImmunizationCoding(immunization);
  if (coding) {
    return `${coding.system}: ${coding.code}`;
  }
  return immunization.vaccineCode?.text || "Unknown";
}

/**
 * Get immunization coding object
 */
export function getImmunizationCoding(immunization: Immunization): Coding | undefined {
  return getFirstCodeSpecified(immunization.vaccineCode?.coding ?? [], ["cvx", "rxnorm"]);
}

/**
 * Get medication code for grouping
 */
export function getMedicationCode(medicationWithRefs: MedicationWithRefs): string {
  const coding = getMedicationCoding(medicationWithRefs);
  if (coding) {
    return `${coding.system}: ${coding.code}`;
  }
  return "-";
}

/**
 * Get medication coding object
 */
export function getMedicationCoding(medicationWithRefs: MedicationWithRefs): Coding | undefined {
  return getFirstCodeSpecified(medicationWithRefs.medication?.code?.coding ?? [], [
    "rxnorm",
    "ndc",
  ]);
}

/**
 * Get condition date for sorting
 */
export function getConditionDate(condition: Condition): string {
  return condition.onsetPeriod?.end || condition.onsetDateTime || condition.recordedDate || "-";
}

/**
 * Get observation date for sorting
 */
export function getObservationDate(observation: Observation): string {
  return (
    observation.effectiveDateTime ||
    observation.effectivePeriod?.start ||
    observation.effectivePeriod?.end ||
    observation.issued ||
    "-"
  );
}

/**
 * Get immunization date for sorting
 */
export function getImmunizationDate(immunization: Immunization): string {
  return immunization.occurrenceDateTime || "-";
}

/**
 * Get medication date for sorting
 */
export function getMedicationDate(medicationWithRefs: MedicationWithRefs): string {
  // Use the same logic as getStartDate from medications.ts
  const latestAdministered = medicationWithRefs.administration.sort((a, b) => {
    const aTime = a.effectiveDateTime || a.effectivePeriod?.start || a.effectivePeriod?.end;
    const bTime = b.effectiveDateTime || b.effectivePeriod?.start || b.effectivePeriod?.end;
    return dayjs(bTime || 0).diff(dayjs(aTime || 0));
  })[0];

  const latestStatement = medicationWithRefs.statement.sort((a, b) => {
    const aTime = a.effectiveDateTime || a.effectivePeriod?.start || a.effectivePeriod?.end;
    const bTime = b.effectiveDateTime || b.effectivePeriod?.start || b.effectivePeriod?.end;
    return dayjs(bTime || 0).diff(dayjs(aTime || 0));
  })[0];

  const latestDispensed = medicationWithRefs.dispense.sort((a, b) => {
    const aTime = a.whenHandedOver;
    const bTime = b.whenHandedOver;
    return dayjs(bTime || 0).diff(dayjs(aTime || 0));
  })[0];

  const latestRequest = medicationWithRefs.requests.sort((a, b) => {
    const aTime = a.authoredOn;
    const bTime = b.authoredOn;
    return dayjs(bTime || 0).diff(dayjs(aTime || 0));
  })[0];

  const administeredTime =
    latestAdministered?.effectiveDateTime ||
    latestAdministered?.effectivePeriod?.start ||
    latestAdministered?.effectivePeriod?.end;
  const statementTime =
    latestStatement?.effectiveDateTime ||
    latestStatement?.effectivePeriod?.start ||
    latestStatement?.effectivePeriod?.end;
  const dispensedTime = latestDispensed?.whenHandedOver;
  const requestTime = latestRequest?.authoredOn;

  return administeredTime || statementTime || dispensedTime || requestTime || "-";
}
